import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { DeploymentDetails } from '../../../../models/organization/deployment';
import {
  computeBlockLayout,
  DeploymentBlock as IDeploymentBlock,
} from '../../../../utils/deployment-timeline/layout';
import { blockGradientColor } from '../../../../utils/deployment-timeline/colors';
import { useAppSelector } from '../../../../store';
import { selectRobotsData } from '../../../../store/robots/robots-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { useDeploymentTimelineState } from './useDeploymentTimelineState';
import DeploymentBlock, { toDateInputValue } from './DeploymentBlock';
import DateInput from '@common/components/Inputs/DateInput';
import { DateTime } from 'luxon';
import ConfirmAbortDialog from '@common/components/General/ConfirmAbortDialog';

const DEFAULT_WIDTH_PX = 960;
const MIN_TRACK_PX = 640;
const MIN_BLOCK_PX = 14;
const TRACK_HEIGHT = 28;
const BAND_HEIGHT = 6;
// Widest a handle overhangs the track edge (active ball radius); the scroll rail
// is padded by this so an edge ball at x=0 / x=trackWidth never triggers a scrollbar.
const BALL_OVERHANG_PX = 12;

interface DeploymentTimelineProps {
  deployments: DeploymentDetails[];
  onSave: (updates: IDeploymentBlock[]) => void;
}

const DeploymentTimeline: React.FC<DeploymentTimelineProps> = ({ deployments, onSave }) => {
  const { t } = useTranslation();

  const patchStatus = useAppSelector(
    (state) => selectRobotsData(state).rest.patchDeploymentDatesStatus,
  );

  const todayCeiling = useRef(Date.now()).current;
  const trackRef = useRef<HTMLDivElement>(null);

  const { blocks, dirty, dragEdge, discard, markSaved } = useDeploymentTimelineState(
    deployments,
    todayCeiling,
  );

  const [activeBall, setActiveBall] = useState<{ index: number; edge: 'start' | 'end' } | null>(
    null,
  );
  const [trackWidth, setTrackWidth] = useState(DEFAULT_WIDTH_PX);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);

  useEffect(() => {
    if (patchStatus === HttpFetchStatus.Success) {
      setActiveBall(null);
      markSaved();
    }
  }, [markSaved, patchStatus]);

  useEffect(() => {
    const measure = () => {
      const measured = trackRef.current?.getBoundingClientRect().width ?? 0;
      if (measured > 0) setTrackWidth(measured);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const timelineMin = blocks.length > 0 ? blocks[0].start : todayCeiling;
  const layout = computeBlockLayout(blocks, timelineMin, todayCeiling, trackWidth, MIN_BLOCK_PX);

  const bands = layout.reduce<
    { periodId: string; periodIndex: number; left: number; right: number }[]
  >((accumulated, layoutEntry, index) => {
    const { periodId, periodIndex } = blocks[index];
    if (periodId === null || periodIndex === null) return accumulated;

    const previous = accumulated[accumulated.length - 1];
    const right = layoutEntry.left + layoutEntry.width;

    if (previous != null && previous.periodId === periodId) {
      previous.right = right;
      return accumulated;
    }

    return [...accumulated, { periodId, periodIndex, left: layoutEntry.left, right }];
  }, []);

  const periodCount = new Set(bands.map((band) => band.periodId)).size;

  const pxToDate = (clientX: number): number => {
    const containerLeft = trackRef.current?.getBoundingClientRect().left ?? 0;
    const ratio = (clientX - containerLeft) / trackWidth;
    return timelineMin + ratio * (todayCeiling - timelineMin);
  };

  const handleDrag = (index: number, edge: 'start' | 'end', clientX: number) => {
    dragEdge(index, edge, pxToDate(clientX));
  };

  const handleManualDateChange = (newDate: DateTime | null) => {
    if (activeBall === null || newDate == null) return;
    dragEdge(activeBall.index, activeBall.edge, newDate.toJSDate().getTime());
  };

  const saveClickHandler = () => {
    setIsConfirmSaveOpen(true);
  };

  const abortClickHandler = () => {
    setIsConfirmSaveOpen(false);
  };

  const confirmSaveClickHandler = () => {
    onSave(
      blocks.map((block) => ({
        id: block.id,
        start: new Date(block.start).toISOString(),
        end: block.end === null ? null : new Date(block.end).toISOString(),
      })),
    );
    setIsConfirmSaveOpen(false);
  };

  const handleDiscard = () => {
    setActiveBall(null);
    discard();
  };

  const activeEntry = activeBall !== null ? blocks[activeBall.index] : null;
  const activeColor =
    activeBall !== null ? blockGradientColor(activeBall.index, blocks.length) : undefined;
  const activeDateMs =
    activeBall !== null && activeEntry
      ? activeBall.edge === 'start'
        ? activeEntry.start
        : activeEntry.end
      : null;

  return (
    <React.Fragment>
      <Stack spacing={2}>
        <Box
          sx={(theme) => ({
            overflowX: 'auto',
            py: 1.5,
            px: `${BALL_OVERHANG_PX}px`,
            borderRadius: 2,
            backgroundColor: theme.palette.background.default,
            border: '1px solid rgba(0,0,0,0.06)',
          })}
        >
          <Box
            ref={trackRef}
            onPointerDown={() => setActiveBall(null)}
            sx={(theme) => ({
              position: 'relative',
              width: '100%',
              minWidth: MIN_TRACK_PX,
              height: TRACK_HEIGHT,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
            })}
            data-testid="deployment-timeline"
          >
            {bands.map((band) => (
              <Box
                key={`${band.periodId}-${band.left}`}
                data-testid="deployment-period-band"
                sx={{
                  position: 'absolute',
                  top: -BAND_HEIGHT - 2,
                  left: band.left,
                  width: Math.max(band.right - band.left, 0),
                  height: BAND_HEIGHT,
                  borderRadius: BAND_HEIGHT / 2,
                  backgroundColor: blockGradientColor(band.periodIndex, periodCount),
                  opacity: 0.45,
                  pointerEvents: 'none',
                }}
              />
            ))}
            {layout.map((layoutEntry, index) => (
              <DeploymentBlock
                key={layoutEntry.id}
                entry={blocks[index]}
                left={layoutEntry.left}
                width={layoutEntry.width}
                color={blockGradientColor(index, blocks.length)}
                activeEdge={activeBall?.index === index ? activeBall.edge : null}
                onActivateEdge={(edge) => setActiveBall({ index, edge })}
                onDrag={(edge, clientX) => handleDrag(index, edge, clientX)}
              />
            ))}
          </Box>
        </Box>

        <Stack
          alignItems="center"
          spacing={2}
          sx={{ minHeight: 40, flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Box sx={{ flexGrow: 1 }}>
            {activeBall !== null && activeEntry && activeDateMs !== null && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                data-testid="deployment-timeline-editor"
                sx={(theme) => ({
                  display: 'inline-flex',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  border: `2px solid ${activeColor}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                })}
              >
                <Box
                  sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: activeColor }}
                />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {activeEntry.organizationName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {activeBall.edge === 'start'
                    ? t('label-deployment-edit-start')
                    : t('label-deployment-edit-end')}
                </Typography>
                <div>
                  <DateInput
                    date={toDateInputValue(activeDateMs)}
                    onDateTimeChange={handleManualDateChange}
                    inputTestId="deployment-timeline-date-input"
                    width="150px"
                  />
                </div>
                <IconButton
                  size="small"
                  aria-label={t('button-cancel')}
                  data-testid="deployment-timeline-editor-close"
                  onClick={() => setActiveBall(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Box>

          <Stack
            direction="row"
            sx={{ width: { xs: '100%', md: 'inherit' } }}
            spacing={1}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              disabled={!dirty}
              onClick={handleDiscard}
              data-testid="deployment-timeline-discard"
            >
              {t('button-discard')}
            </Button>
            <Button
              variant="contained"
              disabled={!dirty}
              onClick={saveClickHandler}
              data-testid="deployment-timeline-save"
            >
              {t('button-save')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <ConfirmAbortDialog
        isOpen={isConfirmSaveOpen}
        title={t('title-confirm-save-deployments')}
        description={t('info-save-deployments')}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={confirmSaveClickHandler}
        onAbort={abortClickHandler}
      />
    </React.Fragment>
  );
};

export default DeploymentTimeline;
